import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Search, UserRoundPlus, X } from 'lucide-react'
import { tr } from 'i18n'
import { assignTransportDriver, getAvailableDriverPeople, releaseTransportDriver } from 'service/AdminPanelService'
import { getErpErrorMessage } from 'service/ErpService'
import DriverAssignmentRegistration from '../DriverAssignmentRegistration/DriverAssignmentRegistration.tsx'
import s from '../Management.module.scss'
import './DriverAssignmentDialog.scss'
import ActorPinField from 'components/Erp/ActorPinField/ActorPinField.tsx'

export default function DriverAssignmentDialog({ driver, mode, busy, error, onClose, onRun }: any) {
  useTranslation()
  const [tab, setTab] = useState('existing')
  const [pin, setPin] = useState('')
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const [people, setPeople] = useState<any[]>([])
  const [count, setCount] = useState(0)
  const [next, setNext] = useState(false)
  const [selected, setSelected] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [revision, setRevision] = useState(0)
  const dialog = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const previous = document.activeElement as HTMLElement
    dialog.current?.focus()
    return () => previous?.focus()
  }, [])
  useEffect(() => {
    if (mode !== 'assign' || tab !== 'existing') return
    let cancelled = false
    setLoading(true); setLoadError('')
    getAvailableDriverPeople({ page, search: query }).then(({ data }: any) => {
      if (cancelled) return
      setPeople(data.results || []); setCount(data.count || 0); setNext(Boolean(data.next))
    }).catch((failure: any) => { if (!cancelled) setLoadError(getErpErrorMessage(failure)) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [mode, tab, page, query, revision])
  const title = mode === 'release' ? 'Освободить водителя' : driver.employee === null ? 'Назначить водителя' : 'Сменить водителя'
  return <div className={s.overlay} onClick={event => event.stopPropagation()}>
    <div className={`${s.dialog} driver-assignment-dialog`} ref={dialog} tabIndex={-1} role="dialog" aria-modal="true"
      aria-labelledby="driver-assignment-title" onKeyDown={event => { if (event.key === 'Escape' && !busy) onClose() }}>
      <div className="driver-assignment-dialog__heading"><h2 id="driver-assignment-title"><UserRoundPlus size={21} aria-hidden="true" />{tr(title)}</h2>
        <button type="button" className={s.button} aria-label={tr('Закрыть')} disabled={busy} onClick={onClose}><X size={18} /></button></div>
      <div className="driver-assignment-dialog__vehicle"><strong>{driver.vehicle_number}</strong><span>{driver.vehicle_model || ''}</span></div>
      {driver.employee !== null && <p>{tr('Текущий водитель')}: <strong>{driver.employee_name}</strong></p>}
      <p className={s.muted}>{tr('Прошлые погрузки сохраняют водителя, который выполнял рейс.')}</p>
      {error && <div role="alert" className={s.error}>{tr(error)}</div>}
      {mode === 'release' ? <form onSubmit={event => { event.preventDefault(); if (!busy && /^[0-9]{4}$/.test(pin)) onRun(() => releaseTransportDriver(driver.id, driver.employee, pin)) }}>
        <p>{tr('Водитель останется в системе без транспорта. Его можно будет назначить на другой автомобиль.')}</p>
        <ActorPinField value={pin} onChange={setPin} disabled={busy} />
        <div className={s.footer}><button type="button" className={s.button} disabled={busy} onClick={onClose}>{tr('Отмена')}</button>
          <button className={s.primary} disabled={busy || !/^[0-9]{4}$/.test(pin)}>{tr('Освободить водителя')}</button></div>
      </form> : <>
        <div className="driver-assignment-dialog__tabs" role="group" aria-label={tr('Способ назначения')}>
          <button type="button" aria-pressed={tab === 'existing'} disabled={busy} onClick={() => setTab('existing')}>{tr('Выбрать свободного водителя')}</button>
          <button type="button" aria-pressed={tab === 'new'} disabled={busy} onClick={() => setTab('new')}>{tr('Зарегистрировать нового водителя')}</button>
        </div>
        {tab === 'new' ? <DriverAssignmentRegistration driver={driver} busy={busy} onRun={onRun} onClose={onClose}
          onExisting={(person: any) => { setSelected(person); setSearch(person.inn); setQuery(person.inn); setPage(1); setTab('existing') }} /> : <>
          <form className={s.toolbar} onSubmit={event => { event.preventDefault(); setQuery(search.trim()); setPage(1); setRevision(value => value + 1) }}>
            <input aria-label={tr('Поиск свободных водителей')} placeholder={tr('ФИО или ИНН')} value={search} disabled={busy} onChange={event => setSearch(event.target.value)} />
            <button className={s.button} disabled={busy || loading}><Search size={14} />{tr('Найти')}</button>
          </form>
          {loadError && <p role="alert" className={s.error}>{tr(loadError)} <button type="button" className={s.button} onClick={() => setRevision(value => value + 1)}>{tr('Повторить')}</button></p>}
          {loading ? <p role="status">{tr('Загрузка водителей…')}</p> : !loadError && <>
            <p className={s.muted}>{tr('Найдено свободных водителей')}: {count}</p>
            <div className="driver-assignment-dialog__people">
              {!people.length && <p>{tr('Нет водителей без транспорта.')}</p>}
              {people.map(person => <label className="driver-assignment-dialog__person" key={person.employee_id}>
                <input type="radio" name="available-driver" value={person.employee_id} disabled={busy}
                  checked={selected?.employee_id === person.employee_id} onChange={() => setSelected(person)} />
                <span><strong>{person.name}</strong><small>{tr('ИНН')}: {person.inn} · {person.phone || '—'}</small></span>
              </label>)}
            </div>
            <div className="driver-assignment-dialog__pagination"><button type="button" className={s.button} disabled={busy || page === 1} onClick={() => setPage(value => value - 1)}>{tr('Назад')}</button>
              <span>{tr('Страница')} {page}</span><button type="button" className={s.button} disabled={busy || !next} onClick={() => setPage(value => value + 1)}>{tr('Далее')}</button></div>
          </>}
          {selected && <p>{tr('Выбран водитель')}: <strong>{selected.name}</strong> · {selected.inn}</p>}
          <form onSubmit={event => {
            event.preventDefault()
            if (!busy && !loading && selected && /^[0-9]{4}$/.test(pin)) onRun(() => assignTransportDriver(driver.id, selected.employee_id, driver.employee, pin))
          }}>
            <ActorPinField value={pin} onChange={setPin} disabled={busy} />
            <div className={s.footer}><button type="button" className={s.button} disabled={busy} onClick={onClose}>{tr('Отмена')}</button>
              <button type="submit" className={s.primary} disabled={busy || loading || !selected || !/^[0-9]{4}$/.test(pin)}>{tr('Назначить водителя')}</button></div>
          </form>
        </>}
      </>}
    </div>
  </div>
}
